import { useState, useCallback, useRef } from 'react';
import { ScannerService } from '@/utils/api';
import { ReceiptScanResult } from '@/utils/api';

// ============================================
// Types
// ============================================

/**
 * Optional callback invoked during the detecting_location step.
 * Receives the raw scan result and should return an enriched copy
 * with geocoded coordinates and address fields.
 * If not provided, the detecting_location step is skipped entirely.
 */
export type DetectLocationFn = (result: ReceiptScanResult) => Promise<ReceiptScanResult>;

/** Discrete steps in the scanning pipeline */
export type ScanStep =
  | 'idle'
  | 'uploading'
  | 'analyzing'
  | 'detecting_location'
  | 'complete'
  | 'error';

export interface ScanProgress {
  /** Current step in the pipeline */
  step: ScanStep;
  /** Translation key for the step label */
  stepKey: string;
}

export interface UseScannerOptions {
  /**
   * Async function that performs location detection / geocoding.
   * Called during the detecting_location step when the scan result
   * contains a location or address. The returned result replaces
   * the original scan data.
   */
  onDetectLocation?: DetectLocationFn;
}

export interface UseScannerReturn {
  /** Whether a scan is currently in progress */
  scanning: boolean;
  /** Current progress step */
  progress: ScanProgress;
  /** The scan result, if successful */
  scanResult: ReceiptScanResult | null;
  /** Error message, if the scan failed */
  error: string | null;
  /** Scan a receipt image file */
  scanReceipt: (file: File) => Promise<ReceiptScanResult | null>;
  /** Scan a receipt image from a Blob (e.g. from camera capture) */
  scanReceiptBlob: (blob: Blob, filename?: string) => Promise<ReceiptScanResult | null>;
  /** Reset state (clear result, error, and progress) */
  reset: () => void;
}

// ============================================
// Constants
// ============================================

const IDLE_PROGRESS: ScanProgress = {
  step: 'idle',
  stepKey: 'scanner.steps.idle',
};

const STEP_PROGRESS: Record<Exclude<ScanStep, 'idle'>, ScanProgress> = {
  uploading: {
    step: 'uploading',
    stepKey: 'scanner.steps.uploading',
  },
  analyzing: {
    step: 'analyzing',
    stepKey: 'scanner.steps.analyzing',
  },
  detecting_location: {
    step: 'detecting_location',
    stepKey: 'scanner.steps.detectingLocation',
  },
  complete: {
    step: 'complete',
    stepKey: 'scanner.steps.complete',
  },
  error: {
    step: 'error',
    stepKey: 'scanner.steps.error',
  },
};

/** Minimum time (ms) to show each regular step so the UI doesn't flash */
const MIN_STEP_DISPLAY_MS = 600;

/** Minimum time (ms) to show the detecting_location step */
const MIN_LOCATION_DISPLAY_MS = 1500;

// ============================================
// Hook
// ============================================

export const useScanner = (options: UseScannerOptions = {}): UseScannerReturn => {
  const { onDetectLocation } = options;

  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState<ScanProgress>(IDLE_PROGRESS);
  const [scanResult, setScanResult] = useState<ReceiptScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track step timestamps to enforce minimum display time
  const stepTimestampRef = useRef<number>(0);

  /** Ensure at least `minMs` has passed since the current step started */
  const waitForMinDisplay = useCallback(async (minMs: number) => {
    const elapsed = Date.now() - stepTimestampRef.current;
    const remaining = minMs - elapsed;

    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining));
    }
  }, []);

  /** Advance to a new progress step, waiting for the previous step's minimum display time */
  const advanceStep = useCallback(
    async (step: Exclude<ScanStep, 'idle'>, prevStepMinMs = MIN_STEP_DISPLAY_MS) => {
      await waitForMinDisplay(prevStepMinMs);
      stepTimestampRef.current = Date.now();
      setProgress(STEP_PROGRESS[step]);
    },
    [waitForMinDisplay]
  );

  /** Core scanning logic shared by scanReceipt and scanReceiptBlob */
  const performScan = useCallback(
    async (file: File): Promise<ReceiptScanResult | null> => {
      setScanning(true);
      setError(null);
      setScanResult(null);

      try {
        // Step 1: Uploading
        stepTimestampRef.current = Date.now();
        setProgress(STEP_PROGRESS.uploading);

        // Brief pause so the user registers the uploading state
        await new Promise((resolve) => setTimeout(resolve, MIN_STEP_DISPLAY_MS));

        // Step 2: Analyzing (the actual API call happens here)
        await advanceStep('analyzing');

        const result = await ScannerService.scanReceipt(file);

        if (result.didFail()) {
          const errorMessage =
            result.getErrorSummary?.() || 'Failed to scan receipt. Please try again.';
          await advanceStep('error');
          setError(errorMessage);
          return null;
        }

        const data =
          result.getDataFirst?.() ||
          (Array.isArray(result.data) ? result.data[0] : result.data);

        if (!data) {
          await advanceStep('error');
          setError('No data returned from scan. Please try again with a clearer photo.');
          return null;
        }

        let scanData = data as ReceiptScanResult;

        // Step 3: Detect location (only if result has address AND callback is provided)
        const hasLocation = scanData.fields?.location || scanData.fields?.whereDone;

        if (hasLocation && onDetectLocation) {
          await advanceStep('detecting_location');

          try {
            // Run the real geocoding via the parent-provided callback.
            // Use Promise.all with a minimum timer so the step is always
            // visible for at least MIN_LOCATION_DISPLAY_MS, even if
            // geocoding resolves instantly.
            const [enrichedResult] = await Promise.all([
              onDetectLocation(scanData),
              new Promise((resolve) => setTimeout(resolve, MIN_LOCATION_DISPLAY_MS)),
            ]);

            scanData = enrichedResult;
          } catch (locationErr) {
            // Location detection failure is non-critical — proceed with original data
            console.warn('[useScanner] Location detection failed:', locationErr);
          }
        }

        // Step 4: Complete
        await advanceStep('complete');

        setScanResult(scanData);
        return scanData;
      } catch (err: any) {
        const errorMessage = err?.message || 'An unexpected error occurred while scanning.';
        setProgress(STEP_PROGRESS.error);
        setError(errorMessage);
        return null;
      } finally {
        setScanning(false);
      }
    },
    [advanceStep, onDetectLocation]
  );

  /** Scan a receipt from a File */
  const scanReceipt = useCallback(
    async (file: File): Promise<ReceiptScanResult | null> => {
      return performScan(file);
    },
    [performScan]
  );

  /** Scan a receipt from a Blob (e.g. camera capture) */
  const scanReceiptBlob = useCallback(
    async (blob: Blob, filename = 'receipt.jpg'): Promise<ReceiptScanResult | null> => {
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      return performScan(file);
    },
    [performScan]
  );

  /** Reset all state back to idle */
  const reset = useCallback(() => {
    setScanning(false);
    setProgress(IDLE_PROGRESS);
    setScanResult(null);
    setError(null);
  }, []);

  return {
    scanning,
    progress,
    scanResult,
    error,
    scanReceipt,
    scanReceiptBlob,
    reset,
  };
};